import  { useCallback, useEffect, useState } from 'react'

import { ArrowDown, Copy, Pencil, TrashBin } from '@gravity-ui/icons'
import { Button, Chip, cn, Spinner, Table, Tabs } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { reportsApi } from '@/api/reports.api'
import { Card, CardContent } from '@/components/ui/Card'
import { ConfirmCloneDialog } from '@/components/ui/ConfirmCloneDialog'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import useGoBack from '@/hooks/useGoBack'
import { mapApiToUi, useReportsStore } from '@/store/reports.store'
import type { Report as UiReport } from '@/types/report.types'
import { formatPLN } from '@/utils/currency'
import { formatDate } from '@/utils/dates'
import { highlightMatch } from '@/utils/text'


interface ReportInfoCardProps {
  label: string;
  value: string | number;
  subValue?: string | number | null;
}

function ReportInfoCard({ label, value, subValue }: ReportInfoCardProps) {
  return (
    <Card>
      <CardContent className="p-2">
        <div className="text-sm font-semibold mb-2 text-zinc-500 dark:text-zinc-400">{label}</div>
        <div className="text-xl font-black text-zinc-700 dark:text-zinc-300">{value}</div>
        {subValue && <div className="text-sm mt-1.5  text-zinc-400 dark:text-zinc-500">{subValue}</div>}
      </CardContent>
    </Card>
  )
}


export default function ReportView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goBack = useGoBack('/reports')
  const { id } = useParams<{ id: string }>()
  const { reports, deleteReport, duplicateReport, filters } = useReportsStore()
  const documentSearch = filters.document


  const [report, setReport] = useState<UiReport | null>(reports.find(r => r.id === id) || null)
  const [isLoading, setIsLoading] = useState(!report)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCloneOpen, setIsCloneOpen] = useState(false)
  const [isCloning, setIsCloning] = useState(false)

  const handleDownload = useCallback(async () => {
    if (!report) return
    const driverName = report.driverName ?? 'driver'
    try {
      const blob = await reportsApi.generateReport(report.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `${driverName}-${report.dateFrom}-${report.dateTo}.xls`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download report:', error)
    }
  }, [report])

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      await deleteReport(id)
      setIsDeleteOpen(false)
      navigate('/reports')
    } catch (error) {
      console.error('Failed to delete report:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [id, deleteReport, navigate])

  const handleCloneConfirm = useCallback(async () => {
    if (!id) return
    setIsCloning(true)
    try {
      const newReportId = await duplicateReport(id)
      setIsCloneOpen(false)
      navigate(`/reports/${newReportId}/edit`)
    } catch (error) {
      console.error('Failed to clone report:', error)
    } finally {
      setIsCloning(false)
    }
  }, [id, duplicateReport, navigate])

  useEffect(() => {
    const loadData = async () => {
      if (!id) return

      setIsLoading(true)
      try {
        const reportResponse = await reportsApi.getReport(id)
        setReport(mapApiToUi(reportResponse))
      } catch (error) {
        console.error('Failed to fetch report details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner color="accent" />
          <span className="text-sm text-zinc-500">{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="p-8 text-zinc-400 text-center">
        <p className="mb-4">{t('reports.not_found')}</p>
        <Button variant="ghost" onClick={goBack}>
          {t('common.back')}
        </Button>
      </div>
    )
  }

  const hasPaidRoads = report.paidRoads && report.paidRoads.length > 0
  const hasFuel = report.fuel && report.fuel.length > 0

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        onBack={goBack}
        title={`${t('reports.view_report')} ${report.identifier || report.reportNumber}`}
        subtitle={report.companyName}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => {
              navigate(`/reports/${id}/edit`)
            }}
            >
              <Pencil className="size-4" />
              {t('common.edit')}
            </Button>
            <Button variant="secondary" onClick={() => {
              setIsCloneOpen(true)
            }}
            >
              <Copy className="size-4" />
              {t('common.clone')}
            </Button>
            <Button variant="secondary" onClick={() => {
              handleDownload()
            }}
            >
              <ArrowDown className="size-4" />
              {t('common.download')}
            </Button>
            <Button variant="danger-soft" onClick={() => {
              setIsDeleteOpen(true)
            }}
            >
              <TrashBin className="size-4" />
              {t('common.delete')}
            </Button>
          </div>
        }
      />

      {/* Info Grid */}
      <div className="grid grid-cols-3 gap-4">
        <ReportInfoCard
          label={t('reports.driver')}
          value={report.driverName || '—'}
          subValue={report.driverPhone}
        />
        <ReportInfoCard
          label={t('reports.vehicle')}
          value={report.vehiclePlate || '—'}
          subValue={report.vehicleBrand}
        />
        <ReportInfoCard
          label={t('common.period')}
          value={`${formatDate(report.dateFrom)} – ${formatDate(report.dateTo)}`}
          subValue={`${t('reports.form.total_rows')}: ${report.rows.length}`}
        />
      </div>

      {/* Tabs */}
      <Tabs className="w-full" variant={'secondary'} defaultSelectedKey="report">
        <Tabs.ListContainer>
          <Tabs.List aria-label="Report content tabs" className="w-fit">
            <Tabs.Tab id="report">
              {t('reports.tab_report')}
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="roads" isDisabled={!hasPaidRoads} className={'w-90'}>
              {t('reports.paid_roads')}

              <Chip className="ml-auto h-5" color={'default'}> {report?.paidRoads?.length ?? 0}</Chip>
              <Tabs.Indicator />
            </Tabs.Tab>
            <Tabs.Tab id="fuel" isDisabled={!hasFuel} className={'w-80'}>
              {t('reports.fuel')}

              <Chip className="ml-auto h-5" color={'default'}> {report?.fuel?.length ?? 0}</Chip>
              <Tabs.Indicator />
            </Tabs.Tab>
          </Tabs.List>
        </Tabs.ListContainer>

        <Tabs.Panel className="pt-4" id="report">
          <Table>
            <Table.ScrollContainer>
              <Table.Content aria-label="Report details table" className="min-w-[900px]">
                <Table.Header>
                  <Table.Column>{t('common.date')}</Table.Column>
                  <Table.Column>{t('reports.table.type')}</Table.Column>
                  <Table.Column>{t('reports.table.doc')}</Table.Column>
                  <Table.Column>{t('reports.table.country')}</Table.Column>
                  <Table.Column>{t('reports.table.amount')}</Table.Column>
                  <Table.Column>{t('reports.table.currency')}</Table.Column>
                  <Table.Column>{t('reports.table.rate')}</Table.Column>
                  <Table.Column>{t('reports.table.nbp_table')}</Table.Column>
                  <Table.Column>{t('reports.table.pln')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {report.rows.map(row => {
                    const isIncluded = row.isIncluded !== false
                    return (
                      <Table.Row
                        key={row.id}
                        className={cn(
                          'transition-colors duration-200',
                          !isIncluded && 'bg-zinc-100/30 dark:bg-zinc-900/30 opacity-50',
                        )}
                      >
                        <Table.Cell>{formatDate(row.date)}</Table.Cell>
                        <Table.Cell>
                          <span className="font-bold text-zinc-800 dark:text-zinc-200">{row.type}</span>
                        </Table.Cell>
                        <Table.Cell>{highlightMatch(row.documentNumber, documentSearch)}</Table.Cell>
                        <Table.Cell>{row.country}</Table.Cell>
                        <Table.Cell>{row.amount.toFixed(2)}</Table.Cell>
                        <Table.Cell>{row.currency}</Table.Cell>
                        <Table.Cell
                          className="font-bold"
                        >{row.exchangeRate.toFixed(4)}</Table.Cell>
                        <Table.Cell>
                          <div className="text-zinc-500 max-w-[80px] truncate" title={row.nbpTable}>
                            {row.nbpTable}
                          </div>
                        </Table.Cell>
                        <Table.Cell className="font-black tabular-nums">
                          {formatPLN(row.amountPLN)}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
            <Table.Footer>
              <div className="flex w-full gap-6 py-2 justify-end">
                <div className="text-right">
                  <div
                    className="text-xs font-black text-zinc-600 dark:text-zinc-400"
                  >{t('reports.form.total_net')}</div>
                  <div
                    className="text-md font-bold tracking-tight text-zinc-500 dark:text-zinc-400 mt-1 flex justify-end gap-3"
                  >
                    <span>{formatPLN(report.totalNetPLN)} PLN</span>
                    <span className="text-zinc-300 dark:text-zinc-600">/</span>
                    <span>{formatPLN(report.totalNetEUR)} EUR</span>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-xs font-black text-zinc-600 dark:text-zinc-400"
                  >{t('reports.form.total_payout')}</div>
                  <div className="text-lg font-black tracking-tight flex justify-end gap-3">
                    <span>{formatPLN(report.totalGrossPLN)} PLN</span>
                    <span className="text-zinc-300 dark:text-zinc-600">/</span>
                    <span>{formatPLN(report.totalGrossEUR)} EUR</span>
                  </div>
                </div>
              </div>
            </Table.Footer>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel className="pt-4" id="roads">
          <Table>
            <Table.ScrollContainer>
              <Table.Content aria-label="Paid roads table" className="min-w-[600px]">
                <Table.Header>
                  <Table.Column>{t('common.date')}</Table.Column>
                  <Table.Column>{t('reports.table.country')}</Table.Column>
                  <Table.Column>{t('reports.table.amount')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {(report.paidRoads ?? []).map(row => (
                    <Table.Row key={row.id}>
                      <Table.Cell>{formatDate(row.date)}</Table.Cell>
                      <Table.Cell>{row.country ? row.country : '-'}</Table.Cell>
                      <Table.Cell className="font-bold tabular-nums">
                        {formatPLN(row.cost)} PLN
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
            <Table.Footer>
              <div className="flex w-full gap-6 py-2 justify-end">
                <div className="text-right">
                  <div
                    className="text-xs font-black text-zinc-600 dark:text-zinc-400"
                  >{t('reports.paid_roads_total')}</div>
                  <div className="text-lg font-black tracking-tight flex justify-end">
                    <span>{formatPLN(report.paidRoadsTotal ?? 0)} PLN</span>
                  </div>
                </div>
              </div>
            </Table.Footer>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel className="pt-4" id="fuel">
          <Table>
            <Table.ScrollContainer>
              <Table.Content aria-label="Fuel table" className="min-w-[700px]">
                <Table.Header>
                  <Table.Column>{t('common.date')}</Table.Column>
                  <Table.Column>{t('reports.table.fuel_type')}</Table.Column>
                  <Table.Column>{t('reports.table.liters')}</Table.Column>
                  <Table.Column>{t('reports.table.country')}</Table.Column>
                  <Table.Column>{t('reports.table.amount')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {(report.fuel ?? []).map(row => (
                    <Table.Row key={row.id}>
                      <Table.Cell>{formatDate(row.date)}</Table.Cell>
                      <Table.Cell>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={row.type === 'diesel' ? 'accent' : row.type === 'adblue' ? 'warning' : 'default'}
                          className="capitalize font-medium"
                        >
                          {t(`reports.fuel_types.${row.type}`, row.type)}
                        </Chip>
                      </Table.Cell>
                      <Table.Cell className="font-semibold tabular-nums">
                        {row.liters.toFixed(2)} L
                      </Table.Cell>
                      <Table.Cell>{row.country ? row.country : '-'}</Table.Cell>
                      <Table.Cell className="font-bold tabular-nums">
                        {formatPLN(row.cost)} PLN
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
            <Table.Footer>
              <div className="flex w-full gap-6 py-2 justify-end">
                <div className="text-right">
                  <div
                    className="text-xs font-black text-zinc-600 dark:text-zinc-400"
                  >{t('reports.fuel_liters_total')}</div>
                  <div className="text-lg font-black tracking-tight flex justify-end">
                    <span>{(report.fuelLitersTotal ?? 0).toFixed(2)} L</span>
                  </div>
                </div>
                <div className="text-right">
                  <div
                    className="text-xs font-black text-zinc-600 dark:text-zinc-400"
                  >{t('reports.fuel_total')}</div>
                  <div className="text-lg font-black tracking-tight flex justify-end">
                    <span>{formatPLN(report.fuelTotal ?? 0)} PLN</span>
                  </div>
                </div>
              </div>
            </Table.Footer>
          </Table>
        </Tabs.Panel>
      </Tabs>

      <div className="flex justify-end gap-3 pt-2">
        <Button variant="ghost" onClick={goBack}>{t('common.back')}</Button>
      </div>

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('reports.delete_confirm')}
        isLoading={isDeleting}
      />

      <ConfirmCloneDialog
        isOpen={isCloneOpen}
        onOpenChange={setIsCloneOpen}
        onConfirm={handleCloneConfirm}
        title={t('reports.clone_report')}
        description={t('reports.clone_confirm')}
        isLoading={isCloning}
      />
    </div>
  )
}
